import React, { useEffect, useState } from 'react';
import {
  Grid, Button, Typography, IconButton, Box, Chip,
  Drawer, Paper, Step, StepLabel, Stepper, Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleTwoToneIcon from '@mui/icons-material/AddCircleTwoTone';
import RemoveCircleTwoToneIcon from '@mui/icons-material/RemoveCircleTwoTone';
import { useForm, useFieldArray, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  FormProvider,
  RHFRadioGroup,
  RHFSearchableSelectField,
  RHFSelectField,
  RHFTextField,
} from '../../../common/hook-form';
import { LoadingButton } from '@mui/lab';
import { FormSkeleton, CourierSkeleton } from '../../../common/Skeletons';
import { apiConstants, getGeneric, postGeneric } from '../../../common/constants';
import { useSnackbar } from 'notistack';
import { useGetMerchantQuery, useLazyGetShipmentDetailQuery } from '../../../redux/commonRTK';
import {
  addressString,
  formatCurrency,
  getMerchantAddressextAPI,
} from '../../../helpers/UtilityHelper';
import RHFPincodeAddressField from '../../../common/hook-form/RHFPincodeAddressField';
import { addDays, format } from 'date-fns';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { useGetCouriersQuery } from '../../../redux/commonRTKPersist';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { NAVY } from '../../../styles/foundation/colors';

// ─── helpers ────────────────────────────────────────────────────────────────

const numTransform = (value, originalValue) => {
  const s = originalValue != null ? String(originalValue).trim() : '';
  return s === '' ? null : value;
};

const defaultItem = { item_name: '', sku_id: '', units: 1, selling_price: '', item_url: '' };

const cloneSchema = Yup.object().shape({
  order_type: Yup.string().optional(),
  payment_mode: Yup.string().required('Please select a payment mode.'),
  invoice_value: Yup.number().transform(numTransform).nullable()
    .min(1, 'Invoice amount must be at least Rs. 1.')
    .required('Please enter the invoice value.'),
  invoice_number: Yup.string().trim().required('Please enter the invoice number.'),
  cod_amount: Yup.number().when('payment_mode', {
    is: 'COD',
    then: Yup.number()
      .transform((v, ov) => { const s = String(ov).replace(/\s+/g, ''); return /^[0-9]*$/.test(s) ? Number(s) : null; })
      .typeError('COD amount must be numeric.')
      .required('COD amount is required for COD orders.')
      .min(1, 'COD amount must be at least Rs. 1.')
      .max(Yup.ref('invoice_value'), 'COD amount cannot exceed invoice value.'),
    otherwise: Yup.number().transform(numTransform).nullable().optional(),
  }),
  rvp_reason: Yup.string().when('order_type', {
    is: 'REVERSE SHIPMENT',
    then: Yup.string().required('RVP reason is mandatory for Reverse Shipment.'),
    otherwise: Yup.string().optional(),
  }),
  qc_type: Yup.string().when('order_type', {
    is: 'REVERSE SHIPMENT',
    then: Yup.string().required('Please select a QC type.'),
    otherwise: Yup.string().optional(),
  }),
  pickupLocation: Yup.string().required('Please select a location.'),
  to_name: Yup.string().required('Please enter customer name.'),
  to_phone_number: Yup.string().required('Please enter phone number.')
    .test('phone-or-masked', 'Phone number must be exactly 10 digits.', (value) => {
      if (!value) return true;
      if (value.includes('*')) return true;
      return /^\d{10}$/.test(value);
    }),
  to_address: Yup.string().required('Please enter address.'),
  to_pincode: Yup.string().required('Please enter pincode.')
    .test('pincode-or-masked', 'Pincode must be exactly 6 digits.', (value) => {
      if (!value) return true;
      if (value.includes('*')) return true;
      return /^\d{6}$/.test(value);
    }),
  to_city: Yup.string().required('Please enter city.'),
  to_state: Yup.string().required('Please enter state.'),
  items: Yup.array().of(
    Yup.object().shape({
      item_name: Yup.string().required('Please enter the item name.'),
      units: Yup.number().min(1, 'Quantity must be at least 1.').required('Please enter the quantity.'),
      sku_id: Yup.string().required('Please enter the SKU ID.'),
      selling_price: Yup.number().transform(numTransform).nullable()
        .min(1, 'Selling price must be at least Rs. 1.').required('Please enter the selling price.'),
    })
  ),
  item_weight: Yup.number().transform(numTransform).nullable().min(1).required('Weight is required.'),
  item_length: Yup.number().transform(numTransform).nullable().min(1).required('Length is required.'),
  item_breadth: Yup.number().transform(numTransform).nullable().min(1).required('Breadth is required.'),
  item_height: Yup.number().transform(numTransform).nullable().min(1).required('Height is required.'),
  courier_type: Yup.string().required('Please select a courier type.'),
  courierPartner: Yup.string().when('courier_type', {
    is: 'Other',
    then: Yup.string().required('Please select a courier partner.'),
    otherwise: Yup.string().optional(),
  }),
});

// ─── Step sub-components (use useFormContext — must live inside <FormProvider>) ───

function StepOrderDetails({ isRvp, setActiveStep }) {
  const { trigger, watch, control } = useFormContext();
  const paymentMode = watch('payment_mode');
  const qcType = watch('qc_type');
  const { fields, append, remove } = useFieldArray({ control, name: 'items' });

  const onNext = async () => {
    const fieldsToValidate = ['payment_mode', 'invoice_value', 'invoice_number', 'items'];
    if (paymentMode === 'COD') fieldsToValidate.push('cod_amount');
    if (isRvp) { fieldsToValidate.push('rvp_reason'); fieldsToValidate.push('qc_type'); }
    if (await trigger(fieldsToValidate)) setActiveStep(3);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={12}>
        <RHFRadioGroup
          label="Payment Mode"
          name="payment_mode"
          options={[{ label: 'PREPAID', value: 'PREPAID' }, { label: 'COD', value: 'COD' }]}
          disabled={isRvp}
        />
      </Grid>
      {isRvp && (
        <Grid item xs={12} md={4}>
          <RHFTextField
            variant="filled"
            name="rvp_reason"
            label="RVP Reason"
            placeholder="Reason for RVP, mandatory for some courier partners."
            required
          />
        </Grid>
      )}
      {isRvp && (
        <Grid item xs={12} md={4}>
          <RHFSelectField
            width="100%"
            name="qc_type"
            label="QC Type"
            mandatory
            options={[
              { label: 'Yes', value: 'doorstep' },
              { label: 'No', value: 'NO' },
            ]}
          />
        </Grid>
      )}
      <Grid item xs={12} md={4}>
        <RHFTextField variant="filled" name="invoice_value" label="Invoice Value"
          placeholder="Total invoice amount" type="number" required />
      </Grid>
      {paymentMode === 'COD' && (
        <Grid item xs={12} md={3}>
          <RHFTextField variant="filled" name="cod_amount" label="COD Amount"
            placeholder="Amount to be collected" required />
        </Grid>
      )}
      <Grid item xs={12} md={4}>
        <RHFTextField variant="filled" name="invoice_number" label="Invoice Number" />
      </Grid>
      <Grid item xs={12} md={4}>
        <RHFTextField variant="filled" name="client_order_id" label="Client Order ID"
          placeholder="Enter client order ID" />
      </Grid>
      {fields.map((field, index) => (
        <Grid item xs={12} key={field.id}>
          <Box sx={{ backgroundColor: '#3783e71a', borderRadius: 1, p: 2, border: '1px solid #ccc' }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} md={3}>
                <RHFTextField variant="filled" name={`items.${index}.sku_id`} label="SKU ID" required />
              </Grid>
              <Grid item xs={12} md={3}>
                <RHFTextField variant="filled" name={`items.${index}.item_name`} label="Name"
                  placeholder="Product name" required />
              </Grid>
              <Grid item xs={12} md={2}>
                <RHFTextField variant="filled" name={`items.${index}.units`} label="Quantity"
                  type="number" required />
              </Grid>
              <Grid item xs={12} md={2}>
                <RHFTextField variant="filled" name={`items.${index}.selling_price`} label="Unit Price"
                  placeholder="Selling price per unit" type="number" required />
              </Grid>
              {isRvp && (
                <Grid item xs={12} md={2}>
                  <RHFTextField variant="filled" name={`items.${index}.item_url`} label="Item URL"
                    placeholder="Enter item image link" required={qcType === 'doorstep'} />
                </Grid>
              )}
              <Grid item xs={12} md={2}>
                {index === fields.length - 1 && (
                  <IconButton size="small" color="primary" onClick={() => append(defaultItem)}>
                    <AddCircleTwoToneIcon />
                  </IconButton>
                )}
                {fields.length > 1 && (
                  <IconButton size="small" color="error" onClick={() => remove(index)}>
                    <RemoveCircleTwoToneIcon />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </Box>
        </Grid>
      ))}
      <Grid item xs={12}>
        <Stack direction="row" columnGap={1} sx={{ mt: 3 }}>
          <Button variant="contained" size="small" color="error" onClick={() => setActiveStep(1)}>Back</Button>
          <Button variant="contained" size="small" onClick={onNext}>Next</Button>
        </Stack>
      </Grid>
    </Grid>
  );
}

function StepLocation({ isRvp, pickupLocationOptions, setActiveStep }) {
  const { trigger } = useFormContext();

  const onNext = async () => {
    if (await trigger(['pickupLocation'])) setActiveStep(2);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sx={{ mb: 1 }}>
        <RHFSearchableSelectField
          options={pickupLocationOptions}
          width="100%"
          placeholder={isRvp ? 'Please Select Drop Location' : 'Please Select Pickup Location'}
          name="pickupLocation"
          autoFocus
        />
        <Typography variant="body2" sx={{ mt: 1 }}>
          If {isRvp ? 'Drop' : 'Pickup'} Location is not present then create a new{' '}
          <Button sx={{ py: 0, textTransform: 'none' }} target="_blank" href="/merchant-step2">
            {isRvp ? 'Drop Location' : 'Pickup Location'}
          </Button>
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Stack direction="row" columnGap={1} sx={{ mt: 3 }}>
          <Button variant="contained" size="small" color="error" onClick={() => setActiveStep(0)}>Back</Button>
          <Button variant="contained" size="small" onClick={onNext}>Next</Button>
        </Stack>
      </Grid>
    </Grid>
  );
}

function StepCustomerDetails({ isRvp, setActiveStep }) {
  const { trigger, watch, setValue } = useFormContext();
  const watchPincode = watch('to_pincode');
  const fetchAddresses = React.useCallback(async () => getMerchantAddressextAPI(watchPincode), [watchPincode]);
  const handleAddressSelect = React.useCallback((addr) => {
    setValue('to_city', addr.city);
    setValue('to_state', addr.state);
    setValue('to_pincode', addr.pincode);
  }, [setValue]);

  const onNext = async () => {
    if (await trigger(['to_name', 'to_phone_number', 'to_address', 'to_pincode', 'to_city', 'to_state'])) {
      setActiveStep(1);
    }
  };

  return (
    <Grid container spacing={2}>
      {isRvp ? (
        <Grid item xs={12}>
          <Typography variant="body2">Who is returning the order? (Sender's Info)</Typography>
        </Grid>
      ) : (
        <Grid item xs={12}>
          <Typography variant="body2">Who is the recipient for the order? (Buyer's Info)</Typography>
        </Grid>
      )}
      <Grid item xs={12} md={4}>
        <RHFTextField variant="filled" name="to_phone_number" label="Phone Number"
          placeholder="Enter buyer's phone number" required />
      </Grid>
      <Grid item xs={12} md={4}>
        <RHFTextField variant="filled" name="to_name" label="Name"
          placeholder="Enter buyer's full name" required />
      </Grid>
      {isRvp && (
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography variant="body2">Where is the order being picked up from? (Sender's Address)</Typography>
        </Grid>
      )}
      {!isRvp && (
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography variant="body2">Where is the order being delivered to? (Buyer's Address)</Typography>
        </Grid>
      )}
      <Grid item xs={12}>
        <RHFTextField variant="filled" name="to_address" label="Complete Address"
          placeholder="House/Floor No. Building Name or Street, Locality" required />
      </Grid>
      <Grid item xs={12} md={4}>
        <RHFPincodeAddressField variant="filled" name="to_pincode" label="Pincode"
          placeholder="Enter pincode" required
          fetchAddresses={fetchAddresses} onAddressSelect={handleAddressSelect} />
      </Grid>
      <Grid item xs={12} md={4}>
        <RHFTextField variant="filled" name="to_city" label="City"
          placeholder="Enter city" required />
      </Grid>
      <Grid item xs={12} md={4}>
        <RHFTextField variant="filled" name="to_state" label="State"
          placeholder="Enter state" required />
      </Grid>
      <Grid item xs={12}>
        <Stack direction="row" columnGap={1} sx={{ mt: 3 }}>
          <Button variant="contained" size="small" onClick={onNext}>Next</Button>
        </Stack>
      </Grid>
    </Grid>
  );
}

function StepPackageDetails({ setActiveStep }) {
  const { trigger } = useFormContext();

  const onNext = async () => {
    if (await trigger(['item_weight', 'item_length', 'item_breadth', 'item_height'])) setActiveStep(4);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="caption" fontWeight={500}>
          <strong>Note:</strong> All package dimensions must be specified in centimeters (cm) &amp; weight in grams
        </Typography>
      </Grid>
      <Grid item xs={12} md={3}>
        <RHFTextField variant="filled" name="item_weight" label="Weight (gms) *" type="number" placeholder="Enter Weight" />
      </Grid>
      <Grid item xs={12} md={3}>
        <RHFTextField variant="filled" name="item_length" label="Length (cm) *" type="number" placeholder="Enter Length" />
      </Grid>
      <Grid item xs={12} md={3}>
        <RHFTextField variant="filled" name="item_breadth" label="Breadth (cm) *" type="number" placeholder="Enter Breadth" />
      </Grid>
      <Grid item xs={12} md={3}>
        <RHFTextField variant="filled" name="item_height" label="Height (cm) *" type="number" placeholder="Enter Height" />
      </Grid>
      <Grid item xs={12}>
        <Stack direction="row" columnGap={1} sx={{ mt: 3 }}>
          <Button variant="contained" size="small" color="error" onClick={() => setActiveStep(2)}>Back</Button>
          <Button variant="contained" size="small" onClick={onNext}>Next</Button>
        </Stack>
      </Grid>
    </Grid>
  );
}

function StepSelectCourier({ newCourier, isLoadingCourier, setActiveStep, isSubmitting }) {
  const { watch, setValue } = useFormContext();
  const { blendedData } = useSelector((state) => state?.report);
  const courierSelection = watch('courier_type');

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {blendedData ? (
          <RHFRadioGroup label="Courier Selection" name="courier_type" options={[
            { label: 'As per Recommendation Rules', value: 'Prozo Choice' },
          ]} />
        ) : (
          <RHFRadioGroup label="Courier Selection" name="courier_type" options={[
            { label: 'As per Recommendation Rules', value: 'Prozo Choice' },
            { label: 'Other Courier', value: 'Other' },
          ]} />
        )}
      </Grid>
      {courierSelection === 'Other' && (
        <Grid item xs={12} md={10}>
          {isLoadingCourier ? (
            <CourierSkeleton />
          ) : (
            <RHFSelectField
              variant="filled"
              width="100%"
              name="courierPartner"
              label="Courier Partner"
              noOptionsText="No courier partner available for this route"
              options={(newCourier || []).filter((c) => !c.isExternal).map((c) => {
                const hasTat = c.providerMinTatDays !== undefined && c.providerMaxTatDays !== undefined && c.providerMaxTatDays > 0
                return {
                  label: (
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1.5 }}>
                      <Typography sx={{
                        fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY,
                        flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {(c?.parent || c?.name)}{(c?.providerDispatchMode || c?.providerServiceTypePrimary) ? ` - ${c.providerDispatchMode || c.providerServiceTypePrimary}` : ''}
                      </Typography>
                      <Chip label={formatCurrency(c?.priceAfterTax)} size='small'
                        sx={{ fontFamily: FONT, fontSize: '0.68rem', fontWeight: WEIGHT.bold, bgcolor: '#e8f5e9', color: '#2e7d32', flexShrink: 0 }}
                      />
                      <Typography sx={{ fontFamily: FONT, fontSize: '0.68rem', fontWeight: WEIGHT.medium, color: NAVY, flexShrink: 0, minWidth: 90, textAlign: 'center' }}>
                        {hasTat ? `TAT: ${c.providerMinTatDays}\u2013${c.providerMaxTatDays}d` : 'TAT: N/A'}
                      </Typography>
                      <Chip label={hasTat ? `EDD: ${c.minDeliveryDate}\u2013${c.maxDeliveryDate}` : 'EDD: N/A'} size='small'
                        sx={{ fontFamily: FONT, fontSize: '0.68rem', fontWeight: WEIGHT.bold, bgcolor: '#e3f2fd', color: '#1565c0', flexShrink: 0 }}
                      />
                    </Box>
                  ),
                  value: c.prozoId,
                }
              })}
            />
          )}
        </Grid>
      )}
      <Grid item xs={12}>
        <Stack direction="row" columnGap={1} sx={{ mt: 3 }}>
          <Button variant="contained" size="small" color="error" onClick={() => setActiveStep(3)}>Back</Button>
          <LoadingButton loading={isSubmitting} disabled={isSubmitting} variant="contained" size="small" type="submit">
            Clone Shipment
          </LoadingButton>
        </Stack>
      </Grid>
    </Grid>
  );
}

// ─── Steps config ────────────────────────────────────────────────────────────

const FORWARD_STEPS = ['Customer Details', 'Pickup Details', 'Order Details', 'Package Details', 'Select Courier'];
const RVP_STEPS    = ['Customer Details', 'Drop Details',   'Order Details', 'Package Details', 'Select Courier'];

// ─── Main component ───────────────────────────────────────────────────────────

const CloneWithEditModal = ({ open, onClose, orderId, orderData, merchantId, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data: allCouriers } = useGetCouriersQuery();
  const [getShipmentDetail] = useLazyGetShipmentDetailQuery();
  const { data: merchantData } = useGetMerchantQuery({ id: merchantId });

  const pickupLocations = (merchantData?.pickup_details || []).filter((loc) => !loc.is_deleted);
  const pickupLocationOptions = _(pickupLocations)
    .filter({ status: 'ACTIVE', address_type: 'PICKUP' })
    .map((loc) => ({ value: loc.id, label: addressString(loc) }))
    .value();

  const [activeStep, setActiveStep] = useState(0);
  const [orderDetails, setOrderDetails] = useState(null);
  const [newCourier, setNewCourier] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm({
    defaultValues: {
      order_type: '',
      payment_mode: '',
      invoice_value: '',
      invoice_number: '',
      cod_amount: '',
      client_order_id: '',
      rvp_reason: '',
      qc_type: '',
      pickupLocation: '',
      to_name: '',
      to_phone_number: '',
      to_address: '',
      to_pincode: '',
      to_city: '',
      to_state: '',
      items: [defaultItem],
      item_length: '',
      item_breadth: '',
      item_height: '',
      item_weight: '',
      courier_type: '',
      dispatch_mode: 'ANY',
      courierPartner: '',
    },
    resolver: yupResolver(cloneSchema),
  });

  const { handleSubmit, watch, reset, setValue } = methods;
  const courierSelection = watch('courier_type');
  const dispatch_mode = watch('dispatch_mode');
  const itemWeight = watch('item_weight');
  const itemLength = watch('item_length');
  const itemBreadth = watch('item_breadth');
  const itemHeight = watch('item_height');

  const isRvp = orderDetails?.order_type === 'REVERSE SHIPMENT';
  const steps = isRvp ? RVP_STEPS : FORWARD_STEPS;

  // ── Fetch order and pre-fill ──────────────────────────────────────────────
  useEffect(() => {
    if (orderId && open) {
      setIsFetching(true);
      getShipmentDetail({
        excludes: 'sla,mongoLabelSignedUrlDetails,orderManifest,courierPricing,mongoInvoiceSignedUrlDetails,courierDetail,courier,courierId,merchant,merchantPricing,order_history',
        andfilter: [{ field: 'awbNumber', operator: 'eq', value: orderId }],
        offset: 0,
        limit: 1,
      }).then((res) => {
        const data = res.data?.data?.[0];
        if (data) {
          setOrderDetails(data);
          const isRvpOrder = data.order_type === 'REVERSE SHIPMENT';
          const matchedLocation = pickupLocations.find((loc) =>
            isRvpOrder
              ? String(loc.pincode) === String(data.delivery_details?.to_pincode)
              : (loc.pincode === data.pickup_details?.from_pincode && loc.name === data.pickup_details?.from_name)
          );
          reset({
            order_type: data.order_type || '',
            payment_mode: isRvpOrder ? 'PREPAID' : (data.payment_mode || ''),
            invoice_value: data.invoice_value || '',
            invoice_number: data.invoice_number || '',
            cod_amount: data.cod_amount || '',
            client_order_id: data.client_order_id || '',
            rvp_reason: data.rvp_reason || '',
            qc_type: data.qc_type || '',
            pickupLocation: matchedLocation?.id || '',
            to_name: isRvpOrder ? (data.pickup_details?.from_name || '') : (data.delivery_details?.to_name || ''),
            to_phone_number: isRvpOrder ? (data.pickup_details?.from_phone_number || '') : (data.delivery_details?.to_phone_number || ''),
            to_address: isRvpOrder ? (data.pickup_details?.from_address || '') : (data.delivery_details?.to_address || ''),
            to_pincode: isRvpOrder ? (data.pickup_details?.from_pincode || '') : (data.delivery_details?.to_pincode || ''),
            to_city: isRvpOrder ? (data.pickup_details?.from_city || '') : (data.delivery_details?.to_city || ''),
            to_state: isRvpOrder ? (data.pickup_details?.from_state || '') : (data.delivery_details?.to_state || ''),
            items: data.item_list?.length
              ? data.item_list.map((item) => ({
                  item_name: item.item_name || '',
                  sku_id: item.sku_id || '',
                  units: item.units || 1,
                  selling_price: item.selling_price || '',
                  item_url: item.item_url || '',
                }))
              : [defaultItem],
            item_length: data.shipment_detail?.[0]?.item_length || '',
            item_breadth: data.shipment_detail?.[0]?.item_breadth || '',
            item_height: data.shipment_detail?.[0]?.item_height || '',
            item_weight: data.shipment_detail?.[0]?.item_weight || '',
            courier_type: '',
            dispatch_mode: data.dispatch_mode || 'ANY',
            courierPartner: '',
          });
        }
      }).finally(() => setIsFetching(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, open]);

  // Re-match pickup location once merchant data resolves
  useEffect(() => {
    if (!orderDetails || !pickupLocations.length) return;
    const isRvpOrder = orderDetails.order_type === 'REVERSE SHIPMENT';
    const match = pickupLocations.find((loc) =>
      isRvpOrder
        ? String(loc.pincode) === String(orderDetails.delivery_details?.to_pincode)
        : (loc.pincode === orderDetails.pickup_details?.from_pincode && loc.name === orderDetails.pickup_details?.from_name)
    );
    if (match) setValue('pickupLocation', match.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetails, pickupLocations.length]);

  // Fetch courier rates when "Other" is selected
  useEffect(() => {
    if (orderData && courierSelection === 'Other') {
      setIsLoading(true);
      getGeneric(`${apiConstants.GET_PINCODES}?merchantOid=${merchantId}&awbNumber=${orderData?.awb_number}`)
        .then((res) => callShipmentRateCalculator(res?.data))
        .catch(() => {
          enqueueSnackbar('Error fetching pincode data', { variant: 'error' });
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courierSelection, orderData, dispatch_mode, itemWeight, itemLength, itemBreadth, itemHeight]);

  const callShipmentRateCalculator = (pincodeResponse) => {
    const length = Number(itemLength) || 0;
    const breadth = Number(itemBreadth) || 0;
    const height = Number(itemHeight) || 0;
    const weight = Number(itemWeight) || 0;

    postGeneric(apiConstants.SHIPMENT_RATE_CALCULATOR, {
      orderType: isRvp ? 'RVP' : 'FORWARD',
      to: parseInt(isRvp ? pincodeResponse?.pickupPincode : pincodeResponse?.deliveryPincode),
      from: parseInt(isRvp ? pincodeResponse?.deliveryPincode : pincodeResponse?.pickupPincode),
      paymentType: isRvp ? 'PREPAID' : (orderData?.payment_mode?.toLowerCase() === 'prepaid' ? 'PREPAID' : 'COD'),
      length, breadth, height, weight,
      ...(dispatch_mode !== 'ANY' ? { dispatchMode: dispatch_mode } : {}),
      codAmount: Number(orderData?.cod_amount) || 0,
      isMerchant: true,
      onlyCouriersAsPerRuleMapping: true, checkServiceable: true,
      merchantOid: merchantId,
    })
      .then((res) => {
        setIsLoading(false);
        const pricing = res?.data;
        const now = new Date();
        const courierType = isRvp ? 'RVP' : 'FORWARD';
        const filtered = allCouriers
          ?.map((d) => {
            const c = { ...d };
            pricing.forEach((t) => {
              if (t.provider?.id === c.prozoId) {
                c.priceAfterTax = t.priceAfterTax;
                c.providerMinTatDays = t.providerMinTatDays;
                c.providerMaxTatDays = t.providerMaxTatDays;
                c.parent = t.provider?.parent;
                c.providerDispatchMode = t.provider?.dispatch_mode;
                c.providerServiceTypePrimary = t.provider?.service_type_primary;
              }
            });
            return c;
          })
          ?.filter((item) => item.priceAfterTax && item.type === courierType)
          ?.map((s) => ({
            ...s,
            minDeliveryDate: format(addDays(now, s.providerMinTatDays || 0), 'dd, MMM'),
            maxDeliveryDate: format(addDays(now, s.providerMaxTatDays || 0), 'dd, MMM'),
          }));
        // Deduplicate by parent + dispatch mode — keep the cheapest entry per combination
        const parentMap = new Map();
        (filtered || []).forEach(c => {
          const key = (c.parent || c.name) + '|' + (c.providerDispatchMode || '');
          const existing = parentMap.get(key);
          if (!existing || c.priceAfterTax < existing.priceAfterTax) {
            parentMap.set(key, c);
          }
        });
        setNewCourier(Array.from(parentMap.values()));
      })
      .catch((err) => {
        enqueueSnackbar(err?.response?.data?.message || 'Something went wrong', { variant: 'error' });
        setIsLoading(false);
      });
  };

  const handleClose = () => {
    reset();
    setOrderDetails(null);
    setNewCourier([]);
    setActiveStep(0);
    onClose();
  };

  const onSubmit = (fill) => {
    if (!orderDetails) return;

    const loc = pickupLocations.find((l) => l.id === fill.pickupLocation);
    const locDetails = loc ? {
      name: loc.name, address_line: loc.address_line, city: loc.city, state: loc.state,
      country: loc.country, pincode: loc.pincode, contactNumber: loc.contactNumber,
      email: loc.email, gstin: loc.gstin,
    } : {};

    const payload = {
      shipment_detail: [{
        item_length: Number(fill.item_length),
        item_breadth: Number(fill.item_breadth),
        item_height: Number(fill.item_height),
        item_weight: Number(fill.item_weight),
      }],
      clonedOrderReference: orderDetails.reference,
      merchantOId: merchantId,
      order_type: orderDetails.order_type,
      shipment_type: orderDetails.shipment_type,
      payment_mode: fill.payment_mode,
      invoice_value: Number(fill.invoice_value),
      invoice_number: fill.invoice_number,
      cod_amount: fill.payment_mode === 'PREPAID' ? 0 : Number(fill.cod_amount),
      client_order_id: fill.client_order_id,
      ...(isRvp && fill.rvp_reason ? { rvp_reason: fill.rvp_reason } : {}),
      ...(isRvp && fill.qc_type && fill.qc_type !== 'NO' ? { qc_type: fill.qc_type } : {}),
      ...(isRvp ? { channel_name: fill.qc_type === 'doorstep' ? 'QC' : 'NONQC' } : {}),
      // For RVP: warehouse (dropdown) → delivery_details; For Forward: warehouse → pickup_details
      ...(loc ? (isRvp ? {
        delivery_details: {
          to_name: locDetails.name, to_address: locDetails.address_line,
          to_city: locDetails.city, to_state: locDetails.state,
          to_country: locDetails.country, to_pincode: locDetails.pincode,
          to_phone_number: locDetails.contactNumber, to_email: locDetails.email,
        },
      } : {
        pickup_details: {
          from_name: locDetails.name, from_address: locDetails.address_line,
          from_city: locDetails.city, from_state: locDetails.state,
          from_country: locDetails.country, from_pincode: locDetails.pincode,
          from_phone_number: locDetails.contactNumber, from_email: locDetails.email,
          gstin: locDetails.gstin,
        },
      }) : {}),
      ...(isRvp ? {
        pickup_details: {
          from_name: fill.to_name, from_phone_number: fill.to_phone_number,
          from_address: fill.to_address, from_pincode: fill.to_pincode,
          from_city: fill.to_city, from_state: fill.to_state,
          from_email: orderDetails.pickup_details?.from_email || '',
          from_country: orderDetails.pickup_details?.from_country || '',
        },
      } : {
        delivery_details: {
          to_name: fill.to_name, to_phone_number: fill.to_phone_number,
          to_address: fill.to_address, to_pincode: fill.to_pincode,
          to_city: fill.to_city, to_state: fill.to_state,
        },
      }),
      item_list: fill.items.map((item) => ({
        item_name: item.item_name, sku_id: item.sku_id,
        units: Number(item.units), selling_price: Number(item.selling_price),
        ...(item.item_url ? { item_url: item.item_url } : {}),
      })),
      skipRule: fill.courier_type === 'Other',
      ...(fill.dispatch_mode && fill.dispatch_mode !== 'ANY' ? { dispatch_mode: fill.dispatch_mode } : {}),
    };

    if (fill.courier_type === 'Other' && fill.courierPartner) {
      payload.courier_id = { prozoId: fill.courierPartner };
    }

    setIsSubmitting(true);
    return postGeneric(apiConstants.CLONE_ORDER_WITH_EDIT, payload)
      .then((res) => {
        if (res.status === 200) {
          enqueueSnackbar(res?.data?.meta?.message || 'Order cloned successfully', { variant: 'success' });
          handleClose();
          if (onSuccess) onSuccess();
        }
      })
      .catch((err) => {
        enqueueSnackbar(
          err?.response?.data?.message || 'Something went wrong while cloning the order',
          { variant: 'error' }
        );
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: '70%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#f0f2f5',
        },
      }}
    >
      {/* ── Header ── */}
      <Box sx={{
        px: 3, py: 2, flexShrink: 0,
        bgcolor: 'white',
        borderBottom: '1px solid', borderColor: 'divider',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="overline" color="text.disabled" sx={{ letterSpacing: 2, fontSize: 10, lineHeight: 1 }}>
              {orderId}
            </Typography>
            <Typography variant="h5" fontWeight={800} color="text.primary" sx={{ mt: 0.5, letterSpacing: -0.5 }}>
              Clone &amp; Edit Order
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Review and modify details before placing
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Chip
              label={isRvp ? 'RVP ORDER' : 'FORWARD ORDER'}
              size="small"
              color={isRvp ? 'warning' : 'primary'}
              sx={{ fontWeight: 700, fontSize: 10, letterSpacing: 0.8 }}
            />
            <IconButton onClick={handleClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        {isFetching ? (
          <FormSkeleton />
        ) : (
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} sx={{ alignItems: 'center' }}>

              {/* Vertical stepper */}
              <Grid item xs={12} md={2}>
                <Stepper orientation="vertical" activeStep={activeStep}>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Grid>

              {/* Step content */}
              <Grid item xs={12} md={10}>
                <Box sx={{ p: 5 }} component={Paper} elevation={1}>
                  <Typography sx={{ mb: 3 }} variant="h6">
                    {activeStep + 1}. {steps[activeStep]}
                  </Typography>

                  {activeStep === 0 && (
                    <StepCustomerDetails isRvp={isRvp} setActiveStep={setActiveStep} />
                  )}
                  {activeStep === 1 && (
                    <StepLocation
                      isRvp={isRvp}
                      pickupLocationOptions={pickupLocationOptions}
                      setActiveStep={setActiveStep}
                    />
                  )}
                  {activeStep === 2 && (
                    <StepOrderDetails isRvp={isRvp} setActiveStep={setActiveStep} />
                  )}
                  {activeStep === 3 && (
                    <StepPackageDetails setActiveStep={setActiveStep} />
                  )}
                  {activeStep === 4 && (
                    <StepSelectCourier
                      newCourier={newCourier}
                      isLoadingCourier={isLoading}
                      setActiveStep={setActiveStep}
                      isSubmitting={isSubmitting}
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          </FormProvider>
        )}
      </Box>
    </Drawer>
  );
};

export default CloneWithEditModal;
